import { type JSX, type ReactNode } from 'react';

import { type SitePageKey, useOptionalNavigator } from '../discovery/router';
import { usePageMeta } from '../discovery/page-meta';
import { useSession } from '../session/SessionProvider';
import {
  BackLink,
  CheckingPanel,
  ComingSoonPanel,
  type GateCopy,
  SignInGate,
  UnreachablePanel,
} from './panels';
import { AccountPage } from './account/AccountPage';
import { ClinicsPage } from './account/ClinicsPage';
import { BookingsPage } from './bookings/BookingsPage';
import { CheckoutPage } from './checkout/CheckoutPage';
import { CouriersPage } from './couriers/CouriersPage';
import { HelpPage } from './help/HelpPage';
import { OrdersPage } from './orders/OrdersPage';
import { PartnerPage } from './partner/PartnerPage';
import { PlacesPage } from './places/PlacesPage';
import { PrescriptionsPage } from './prescriptions/PrescriptionsPage';
import { RewardsPage } from './rewards/RewardsPage';
import { SiteFooter } from './SiteFooter';
import { SiteHeader, type StoreHeaderParts } from './SiteHeader';

/**
 * The website pages' shell: the frames' header, the page, the frames' footer.
 *
 * Only these pages use it. The homepage keeps its own chrome, unchanged.
 */
export function SiteShell({
  children,
  store,
  bleed,
  bare = false,
}: {
  readonly children: ReactNode;
  /** The Store frame draws a location picker and basket pill in the header, on a white page. */
  readonly store?: StoreHeaderParts;
  /** A page whose bands run edge to edge (Partner with us): no page width or padding on main. */
  readonly bleed?: boolean;
  /** A full-width page on white that lays out its own sections (Become a courier). */
  readonly bare?: boolean;
}): JSX.Element {
  return (
    <div className={`jt-site${store !== undefined ? ' jt-site--store' : ''}${bare ? ' jt-site--white' : ''}`}>
      <a className="jt-skip" href="#main">
        Skip to main content
      </a>
      <SiteHeader {...(store !== undefined ? { store } : {})} />
      <main
        id="main"
        className={`jt-site-main${store !== undefined ? ' jt-site-main--store' : ''}${bleed === true ? ' jt-site-main--bleed' : ''}${bare ? ' jt-site-main--bare' : ''}`}
      >
        {children}
      </main>
      <SiteFooter />
    </div>
  );
}

interface PageSpec {
  /** The page heading, as the frame writes it. */
  readonly title: string;
  /** The browser tab title, as the frame's <title> writes it. */
  readonly tabTitle: string;
  readonly subtitle?: string;
  /** Pages that are personal draw the frame's logged-out panel to a visitor who is not logged in. */
  readonly gate?: GateCopy;
}

/**
 * The ten destinations this stage routes (the Store is the existing storefront
 * page). Gate copy is the frames' own, except where it names something the
 * owner has ruled the platform does not offer yet — those lines are trimmed and
 * listed in the hand-off:
 *
 * - Rewards: "collect voucher stamps and use account credit" (D8 — Rewards is
 *   the PENNY tab only), and the subtitle's "voucher cards and account credit".
 * - Prescriptions: "NHS" before "repeat prescriptions" (D10 — NHS is a separate
 *   project).
 * - Account: "payment methods" (D2 — saved cards are deferred).
 */
export const SITE_PAGE_SPECS: Readonly<Record<SitePageKey, PageSpec>> = {
  account: {
    title: 'Your account',
    tabTitle: 'Your account',
    gate: {
      head: 'Manage your details',
      copy: 'Log in to update your details, addresses and how we contact you.',
    },
  },
  orders: {
    title: 'Orders',
    tabTitle: 'Your orders',
    gate: {
      head: "Everything you've ordered",
      copy: 'Log in to track deliveries live, reorder past items and get help with an order.',
    },
  },
  bookings: {
    title: 'Bookings',
    tabTitle: 'Your bookings',
    gate: {
      head: 'Your appointments and consultations',
      copy: 'Log in to see upcoming appointments, follow your online consultations and reschedule or cancel.',
    },
  },
  rewards: {
    title: 'Rewards',
    tabTitle: 'Rewards',
    subtitle: 'PENNY points',
    gate: { head: 'Unlock and save', copy: 'Log in to see your PENNY balance and how you earn it.' },
  },
  prescriptions: {
    title: 'Prescriptions',
    tabTitle: 'Prescriptions',
    gate: {
      head: 'Your prescriptions, in one place',
      copy: "Log in to order repeat prescriptions, upload a paper prescription and follow every step until it's ready.",
    },
  },
  places: { title: 'Places near you', tabTitle: 'Pharmacies & health services near you' },
  checkout: { title: 'Checkout', tabTitle: 'Checkout' },
  help: { title: 'Help', tabTitle: 'Help' },
  couriers: { title: 'Become a courier', tabTitle: 'Become a courier' },
  partner: { title: 'Partner with us', tabTitle: 'Partner with us' },
};

function comingSoonCopy(spec: PageSpec, email: string | undefined): string {
  const base = `${spec.title} is not on the website yet. We're building it now.`;
  return email === undefined ? base : `${base} You're logged in as ${email}.`;
}

/**
 * One website destination.
 *
 * A personal page shows the frame's logged-out panel to a visitor who is not
 * logged in. Every page whose own content is not built yet says so — it never
 * shows a mock of the finished page.
 */
export function SitePage({ page }: { readonly page: SitePageKey }): JSX.Element {
  const spec = SITE_PAGE_SPECS[page];
  const session = useSession();
  const path = useOptionalNavigator()?.location.path ?? window.location.pathname;

  usePageMeta({
    title: spec.tabTitle,
    // Not indexable: unfinished pages, and personal pages, which are never for a crawler.
    // Partner with us is a finished public page, so it may be.
    noindex: page !== 'partner',
  });

  let body: JSX.Element;
  if (page === 'places') {
    // Stage 7: Places near you is built. A public page — logged in and out —
    // that draws the frame's own header (postcode picker and mode tabs).
    return <PlacesPage />;
  }
  if (page === 'partner') {
    // Stage 13: Partner with us is built. A public page — logged in and out —
    // whose application form starts a real provider application.
    return (
      <SiteShell bleed>
        <PartnerPage />
      </SiteShell>
    );
  }
  if (page === 'checkout') {
    // Stage 6: the Checkout page is built, for a guest and for a signed-in
    // person alike. It draws its own "Back to basket", as the frame does.
    return (
      <SiteShell>
        <CheckoutPage />
      </SiteShell>
    );
  }
  if (page === 'couriers') {
    // Stage 12: Become a courier is built — a public page, logged in and out,
    // whose sign-up applies through the platform's own `driver/apply`.
    return (
      <SiteShell bare>
        <CouriersPage />
      </SiteShell>
    );
  }
  if (page === 'help') {
    // Stage 11: the Help page is built. Public — logged in and out — and it
    // draws its own Back link, as the frame does.
    return (
      <SiteShell>
        <HelpPage />
      </SiteShell>
    );
  }
  if (spec.gate === undefined) {
    body = (
      <ComingSoonPanel
        title={spec.title}
        subtitle={spec.subtitle}
        copy={comingSoonCopy(spec, session.status === 'signedIn' ? session.user?.email : undefined)}
      />
    );
  } else if (session.status === 'idle' || session.status === 'loading') {
    body = <CheckingPanel title={spec.title} />;
  } else if (session.status === 'unreachable') {
    body = <UnreachablePanel title={spec.title} onRetry={session.retry} />;
  } else if (page === 'orders') {
    // Stage 4: the Orders page is built — the frame's logged-out panel with this
    // browser's guest orders, the signed-in list, and one order's detail. It
    // draws its own Back link, as the frame does.
    return (
      <SiteShell>
        <OrdersPage signedIn={session.status === 'signedIn' && session.user !== undefined} />
      </SiteShell>
    );
  } else if (session.status === 'signedOut' || session.user === undefined) {
    body = <SignInGate title={spec.title} subtitle={spec.subtitle} gate={spec.gate} />;
  } else if (page === 'bookings') {
    // Stage 8: the Bookings page is built — appointments and online
    // consultations, the list and one booking's detail. It draws its own Back
    // link, as the frame does.
    return (
      <SiteShell>
        <BookingsPage user={session.user} />
      </SiteShell>
    );
  } else if (page === 'prescriptions') {
    // Stage 10: the Prescriptions page is built — private prescriptions, uploads
    // and repeat requests to a pharmacy (D10 phase 1). NHS nomination, NHS
    // number and GP link are "Coming soon". It draws its own Back link.
    return (
      <SiteShell>
        <PrescriptionsPage user={session.user} />
      </SiteShell>
    );
  } else if (page === 'rewards') {
    // Stage 9: the Rewards page is built — the PENNY tab end to end; voucher
    // cards, account credit and the swap are drawn "Coming soon" (D8). It
    // draws its own Back link, as the frame does.
    return (
      <SiteShell>
        <RewardsPage />
      </SiteShell>
    );
  } else if (page === 'account' && path.startsWith('/account/clinics')) {
    // Clinics A14: "My clinics" — one card per clinic that linked this person as a patient, and each
    // clinic's pre-visit tasks, appointments, messages, photos and aftercare.
    return (
      <SiteShell>
        <ClinicsPage user={session.user} path={path} />
      </SiteShell>
    );
  } else if (page === 'account') {
    // Stage 3: the Account page is built. It draws its own Back link, which
    // returns to the overview from a section, as the frame's does.
    return (
      <SiteShell>
        <AccountPage user={session.user} />
      </SiteShell>
    );
  } else {
    body = (
      <ComingSoonPanel
        title={spec.title}
        subtitle={spec.subtitle}
        copy={comingSoonCopy(spec, session.user.email)}
      />
    );
  }

  return (
    <SiteShell>
      <BackLink />
      {body}
    </SiteShell>
  );
}
